import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getPaymentModeDetail, createPaymentMode, updatePaymentMode } from '../api/paymentModeApi';

export function usePaymentModeEntryScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editingPaymentModeId = searchParams.get('id');

  const [form, setForm] = useState({
    name: '',
    bankName: '',
  });

  const [operationalStatus, setOperationalStatus] = useState('active');
  const [isLoading, setIsLoading] = useState(Boolean(editingPaymentModeId));
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    async function loadIfEditing() {
      if (!editingPaymentModeId) return;
      setIsLoading(true);
      setError('');
      try {
        const detail = await getPaymentModeDetail(editingPaymentModeId);
        if (detail) {
          setForm({
            name: detail.name || '',
            bankName: detail.bankName || '',
          });
          setOperationalStatus(detail.status === 'Active' ? 'active' : 'inactive');
        } else {
          setError('Payment Mode not found.');
        }
      } catch (e) {
        setError('Failed to load payment mode details.');
      } finally {
        setIsLoading(false);
      }
    }
    loadIfEditing();
  }, [editingPaymentModeId]);

  const patchForm = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Payment Mode Name is required.');
      return;
    }
    setIsSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        ...form,
        status: operationalStatus === 'active' ? 'Active' : 'Archive / Deactive'
      };
      if (editingPaymentModeId) {
        await updatePaymentMode(editingPaymentModeId, payload);
        setSuccess('Payment Mode updated successfully.');
      } else {
        await createPaymentMode(payload);
        setSuccess('Payment Mode created successfully.');
      }
      setTimeout(() => {
        navigate('/masters/payment-mode/list');
      }, 1000);
    } catch (e) {
      setError('Failed to save payment mode.');
    } finally {
      setIsSaving(false);
    }
  };

  const completionPercent = useMemo(() => {
    const fields = ['name'];
    const filled = fields.filter(f => form[f]?.trim?.()).length;
    return Math.round((filled / fields.length) * 100);
  }, [form]);

  return {
    state: {
      form,
      operationalStatus,
      isLoading,
      isSaving,
      error,
      success,
      isEditMode: Boolean(editingPaymentModeId),
      completionPercent,
    },
    actions: {
      patchForm,
      setOperationalStatus,
      handleSave,
    }
  };
}
